import { Injectable } from '@angular/core';
import { CheckinBaseClass } from '../../components';
import { CheckinCommonModel } from '../../interface/checkin-common';
import { CheckinSearchResultState } from '../../store';
import { PROPERTY_KEYS } from '@conf/property-keys.config';
import { CHECKIN_ROUTES } from '@conf/routes.config';
import {
  AMCMemberModel,
  AnaBizLoginStatusType,
  AswServiceModel,
  LoginStatusType,
  SessionStorageName,
} from '@lib/interfaces';
import { AswMasterService, AswServiceStoreService, CommonLibService } from '@lib/services';
import { filter, first, map, take, tap } from 'rxjs/operators';
import { lastValueFrom, race, zip } from 'rxjs';
import {
  PostJourneysGetJourneyRequestSearchProperty,
  PostJourneysGetJourneyResponseDataTravelersInner,
  PostJourneysSearchJourneyRequest,
  PostJourneysSearchJourneyResponse,
} from 'src/sdk_checkin';
import { ParameterFromServicing } from '../../interface';
import { CheckinCommonStoreService } from '../checkin-common-store/checkin-common-store.service';
import { CheckinSearchResultStoreService } from '../checkin-search-result-store/checkin-search-result-store.service';
import { CprResultStoreService } from '../cpr-result-store/cpr-result-store.service';
import { LOAD_MASTERS } from '../../master/master';
import { OfficeByOfficeCode } from '../../master/office-by-office-code';
import { PREVIOUS_PAGE_IDENTIFIER } from '@conf/previous-page-identifier.config';
import { ServicingMybookingTransitionService } from '../servicing-mybooking-transition/servicing-mybooking-transition.service';
import { AswInterNationalParameter } from '../../../common/interface/asw-international-parameter';

/**
 * 連携サイトID：遷移元の判定などに用いる
 */
type SiteIds = (typeof SiteIds)[keyof typeof SiteIds];
const SiteIds = {
  /** TOPからの遷移 */
  inflowFromAswTop: 'ASW_TOP',
  /** 予約詳細からの遷移 */
  inflowFromReservation: 'RESERVATION',
};

/** 連携パラメータ */
type LinkageParameter = {
  /** 予約番号 */
  INTRL: string;
  /** 航空券番号 */
  INTTN: string;
  /** AMC会員番号 */
  MN: string;
  /** 搭乗者姓 */
  LN: string;
  /** 搭乗者名 */
  FN: string;
  /** アプリ固有の固定値 */
  SID: 'CKIN';
  /** 遷移元国設定 */
  CNTRY: string;
  /** 遷移元言語設定 */
  LANG: string;
};

const CheckinPropertyCategory = 'checkIn';

@Injectable()
export class CheckinSearchService extends CheckinBaseClass {
  /**
   * 外部流入元
   */
  private _inflowFrom?: SiteIds;
  /** 遷移先URL： able-D */
  private ableDUrl?: string;
  /** 遷移先URL： iCas */
  private iCasUrl?: string;

  constructor(
    private _commonService: CommonLibService,
    private _masterService: AswMasterService,
    private _checkinSearchResultStoreService: CheckinSearchResultStoreService,
    private _cprResultStoreService: CprResultStoreService,
    private _aswServiceStoreService: AswServiceStoreService,
    private _checkinCommonStoreService: CheckinCommonStoreService,
    private _servicingMybookingTransitionService: ServicingMybookingTransitionService
  ) {
    super();

    // able-D、iCasへ遷移するためのURLをプロパティから取得
    this.ableDUrl = this._masterService.getMPropertyByKey(CheckinPropertyCategory, PROPERTY_KEYS.ableDUrl);
    this.iCasUrl = this._masterService.getMPropertyByKey(CheckinPropertyCategory, PROPERTY_KEYS.iCasUrl);
  }

  override destroy(): void {}

  /**
   * チェックイン検索の関連ストアをクリアする
   */
  clearCheckinSearchStore() {
    this._checkinSearchResultStoreService.clear();
    this._cprResultStoreService.clearCprResult();
  }

  /**
   * POSTパラメータで指定された検索条件を取得
   * @returns
   */
  getPostParameter(): PostJourneysSearchJourneyRequest | undefined {
    const rawParam = (window as any).Asw?.ApiRequestParam;
    if (rawParam) {
      return rawParam.post as PostJourneysSearchJourneyRequest;
    }
    return undefined;
  }

  /**
   * SITE_ID=ASW_TOP向け: POSTパラメータで指定された検索条件を取得
   * @returns
   */
  getPostParameterForAswTop(): PostJourneysSearchJourneyRequest | undefined {
    /** 必要なパラメータが含まれているかチェック */
    const hasRequiredPostParameter = (json: PostJourneysSearchJourneyRequest) => {
      return !!json.searchType && (!!json.orderId || !!json.eTicketNumber) && !!json.firstName && !!json.lastName;
    };

    const param = this.getPostParameter();
    return !!param && hasRequiredPostParameter(param) ? param : undefined;
  }

  /**
   * SITE_ID=RESERVATION向け: POSTパラメータで指定された検索条件を取得
   * @returns
   */
  getPostParameterForReservation(): PostJourneysSearchJourneyRequest | undefined {
    /** 必要なパラメータが含まれているかチェック */
    const hasRequiredPostParameter = (json: PostJourneysSearchJourneyRequest) => {
      return (!!json.orderId || !!json.eTicketNumber) && !!json.firstName && !!json.lastName;
    };

    const param = this.getPostParameter();
    if (!!param && hasRequiredPostParameter(param)) {
      // 検索方法と予約詳細からの遷移であるかのフラグを設定
      param.searchType = !!param.orderId
        ? PostJourneysSearchJourneyRequest.SearchTypeEnum.Order
        : PostJourneysSearchJourneyRequest.SearchTypeEnum.ETicket;
      param.transitionedFromServicing = true;
      return param;
    }
    return undefined;
  }

  /**
   * サービス共通情報に格納される検索条件を取得できるObservableを返す
   *
   * @returns
   */
  async getInitialParameterFromCommonStore$(): Promise<PostJourneysSearchJourneyRequest | undefined> {
    /** 必要なパラメータが含まれているかチェック */
    const hasRequiredPostParameter = (aswService: AswServiceModel, checkinCommon: CheckinCommonModel) => {
      return (
        !!checkinCommon.searchType &&
        (!!aswService.orderId || !!aswService.travelDocumentId) &&
        !!aswService.firstName &&
        !!aswService.lastName
      );
    };

    const checkinCommon = await lastValueFrom(this._checkinCommonStoreService.getCheckinCommonState$());
    const aswService = await lastValueFrom(this._aswServiceStoreService.getAswService$());

    // 検索条件が与えられている場合、その検索条件を返す
    if (hasRequiredPostParameter(aswService, checkinCommon)) {
      return Object.assign(
        {
          searchType: checkinCommon.searchType,
          firstName: aswService.firstName,
          lastName: aswService.lastName,
        },
        checkinCommon.searchType == PostJourneysSearchJourneyRequest.SearchTypeEnum.Order
          ? { orderId: aswService.orderId }
          : { eTicketNumber: aswService.travelDocumentId }
      ) as PostJourneysSearchJourneyRequest;
    }

    // それ以外の場合は初期流入なしと判断
    return undefined;
  }

  /**
   * amcMember用の検索条件を取得
   * @returns
   */
  getInitialParameterForRealLogin(amcMember: AMCMemberModel): PostJourneysSearchJourneyRequest {
    return {
      searchType: PostJourneysSearchJourneyRequest.SearchTypeEnum.AmcMember,
      firstName: amcMember.alteaName?.firstName || '',
      lastName: amcMember.alteaName?.lastName || '',
    };
  }

  /**
   * POSTパラメータで指定された流入元を返す
   *
   * @returns 外部流入の場合は流入元SITE_IDを、直接流入及び不明の場合はundefinedを返す
   */
  private getInflowFrom(): string | undefined {
    const rawParam = (window as any).Asw?.ApiRequestParam;
    if (rawParam) {
      const param = rawParam.post as ParameterFromServicing;
      return param?.SITE_ID;
    }
    return undefined;
  }

  /**
   * SITE_ID が ASW TOP からの遷移であればtrueを返す
   * @param siteId 連携サイトID
   * @returns
   */
  isInflowFromAswTop() {
    if (!this._inflowFrom) {
      this._inflowFrom = this.getInflowFrom();
    }
    return this._inflowFrom === SiteIds.inflowFromAswTop;
  }

  /**
   * SITE_ID が RESERVATION (予約詳細) からの遷移であればtrueを返す
   * @param siteId 連携サイトID
   * @returns
   */
  isInflowFromReservation() {
    if (!this._inflowFrom) {
      this._inflowFrom = this.getInflowFrom();
    }
    return this._inflowFrom === SiteIds.inflowFromReservation;
  }

  /**
   * loginStatusからリアルログイン状態かどうかを返す
   *
   * @param loginStatus
   * @returns
   */
  isRealLogin(loginStatus: LoginStatusType) {
    return loginStatus === LoginStatusType.REAL_LOGIN;
  }

  /**
   * loginStatusから仮ログイン状態かどうかを返す
   *
   * @param loginStatus
   * @returns
   */
  isTemporaryLogin(loginStatus: LoginStatusType) {
    return loginStatus === LoginStatusType.TEMPORARY_LOGIN;
  }

  /**
   * CPR取得共通処理
   */
  getCprCommonProcess$(journeyId: string, searchProperty?: PostJourneysGetJourneyRequestSearchProperty) {
    this._cprResultStoreService.callApi$({ journeyId, searchProperty });
    return this._cprResultStoreService.getCprResult$().pipe(filter((model) => !!model));
  }

  /**
   * ガード用のチェックイン検索共通処理
   * @param condition
   * @returns APIのエラーが発生したか、もしくは画面遷移先URL
   */
  checkinSearchCommonProcessOnGuard$(condition: PostJourneysSearchJourneyRequest) {
    // APIのエラーを監視
    const apiError$ = this._checkinSearchResultStoreService.getErrorMessageId$().pipe(
      filter((state) => !!state),
      tap(() => {
        // APIがエラーになった場合、外部流入パラメータを削除する
        this.clearInflowParameter();
      }),
      map(() => true)
    );

    // APIのエラーと正常値で早いものを返す
    return race(apiError$, this.checkinSearchCommonProcess$(condition)).pipe(take(1));
  }

  /**
   * チェックイン検索共通処理
   *
   * 必要な場合に遷移先のURLを返す。
   */
  checkinSearchCommonProcess$(condition: PostJourneysSearchJourneyRequest) {
    // APIを呼び出すたびにstoreの呼び出し状況をクリア
    this.clearCheckinSearchStore();
    this._commonService.errorsHandlerService.clearRetryableError();
    this._commonService.apiErrorResponseService.clearApiErrorResponse();

    // FIXME: 呼び出し側（Component/Service）でURLを扱う処理を書かなくて済むようにしたい。
    // FIXME: Ovservable と Ovservable<Observable> のパターンが混在しており複雑なため改修したい。
    this._checkinSearchResultStoreService.callApi(condition);
    const zip$ = zip(
      this._commonService.amcMemberStoreService.getAMCMember$().pipe(first()),
      this._checkinSearchResultStoreService.getResultStateIfSuccessful$(),
      this._masterService.getAswMasterByKey$(LOAD_MASTERS.OFFICE_BY_OFFICE_CODE.key).pipe(filter((state) => !!state))
    );
    return zip$.pipe(
      tap(() => {
        // チェックイン検索成功時に外部流入パラメータと予約詳細から遷移してきたことを削除する
        this.clearInflowParameter();
        this._servicingMybookingTransitionService.clearFromServicingParameter();
      }),
      map(([amcMember, result, officeByOfficeCode]) => {
        return this.getCheckinSystemUrlOrNavigateCheckinSystem(condition, result, amcMember, officeByOfficeCode);
      })
    );
  }

  /**
   * チェックインシステムのURLを取得、または遷移を行うObservableを取得
   *
   * AbleD: URLを取得する
   * iCas: formを用いて遷移を行う
   * Spaces: URLを取得する
   *
   * @param state
   * @returns
   */
  getCheckinSystemUrlOrNavigateCheckinSystem(
    condition: PostJourneysSearchJourneyRequest,
    result: CheckinSearchResultState,
    amcMember: AMCMemberModel,
    officeByOfficeCode: OfficeByOfficeCode
  ) {
    // NOTE: lastValueFromを使ったasync/awaitな実装にしたい所だが、
    // 呼び出し元コンポーネントのngOnInitで同期処理できないのでobservableを返す。
    if (result.model?.checkinSystem === PostJourneysSearchJourneyResponse.CheckinSystemEnum.AbleD) {
      return this.getAbleDUrl(result);
    } else if (result.model?.checkinSystem === PostJourneysSearchJourneyResponse.CheckinSystemEnum.ICas) {
      return this.navigateICas(condition, officeByOfficeCode, result);
    } else if (result.model?.checkinSystem === PostJourneysSearchJourneyResponse.CheckinSystemEnum.Space) {
      return this.getSpacesUrl(condition, result, amcMember);
    } else {
      // 想定外のcheckinSystem
      return '';
    }
  }

  /**
   * 国内線ASW(able-D)のチェックイン機能へ遷移するURLを取得するobservableを返す
   * @returns
   */
  private getAbleDUrl(result: CheckinSearchResultState) {
    const aswContext = this._commonService.aswContextStoreService.aswContextData;
    this._checkinSearchResultStoreService.clear();
    this._cprResultStoreService.clearCprResult();
    const lang = aswContext.lang === 'ja' || aswContext.lang === 'en' ? aswContext.lang : 'en';

    let param: AswInterNationalParameter = {
      externalFlg: 'ASWINT',
      LANG: lang,
      parameter: result.model?.encryptedDomesticAswParameter,
    };

    if (aswContext.anaBizLoginStatus === AnaBizLoginStatusType.LOGIN) {
      const ANA_BIZ_ACCESS_TOKEN_KEY = 'encryptedAnaBizAccessToken';
      param.anaBizAccessToken = localStorage.getItem(ANA_BIZ_ACCESS_TOKEN_KEY);
    }

    // プロパティから国内線ASW(able-D)のURLを取得する
    const query = this.createQueryString(param);
    return `${this.ableDUrl}?${query}`;
  }

  /**
   * iCasシステムへ遷移する
   * @returns
   */
  private navigateICas(
    searchCondition: PostJourneysSearchJourneyRequest,
    officeByOfficeCode: OfficeByOfficeCode,
    result: CheckinSearchResultState
  ) {
    if (!this.iCasUrl) {
      return undefined;
    }
    this._checkinSearchResultStoreService.clear();

    // 指定のデータをフォームに設定する
    const form = document.createElement('form');

    // iCasに渡すためのクエリパラメータ取得する
    const officeCode = this._commonService.aswContextStoreService.aswContextData.pointOfSaleId;
    const connectionKind = officeByOfficeCode[officeCode]?.[0].connection_kind;
    const identificationId = sessionStorage.getItem(SessionStorageName.IDENTIFICATION_ID);
    const lang = this._commonService.aswContextStoreService.aswContextData.lang;

    form.action = `${this.iCasUrl}?CONNECTION_KIND=${connectionKind}&LANG=${lang}&identificationId=${identificationId}`;
    form.method = 'POST';
    document.body.append(form);
    // submit する直前に発火するイベント
    form.addEventListener('formdata', (event) => {
      const formData = event.formData;
      if (searchCondition.searchType === PostJourneysSearchJourneyRequest.SearchTypeEnum.Order) {
        formData.set('searchTarget:radioGroup', '0');
        const orderId = searchCondition.orderId;
        formData.set('reservationNumber', orderId ? orderId : '');
      } else if (searchCondition.searchType === PostJourneysSearchJourneyRequest.SearchTypeEnum.ETicket) {
        formData.set('searchTarget:radioGroup', '1');
        const eTicketNumber = searchCondition.eTicketNumber;
        formData.set('ticketNumber', eTicketNumber ? eTicketNumber : '');
      } else if (searchCondition.searchType === PostJourneysSearchJourneyRequest.SearchTypeEnum.AmcMember) {
        formData.set('searchTargetAmc', 'true');
        formData.set(
          'amcNumber',
          result.model?.frequentFlyerCard?.cardNumber ? result.model?.frequentFlyerCard?.cardNumber : ''
        );
      }
      if (searchCondition.transitionedFromServicing) {
        formData.set('RESERVATION', 'true');
      }
      const firstName = searchCondition.firstName;
      const lastName = searchCondition.lastName;
      formData.set('passengerFirstName', firstName ? firstName : '');
      formData.set('passengerLastName', lastName ? lastName : '');
      formData.set('STATIC', 'STATIC');
    });
    form.submit();
    // NOTE: submitして遷移するため呼び出し元には何も返さない。
    return undefined;
  }

  /**
   * チェックイン状況詳細画面(C02-P010) または チェックイン対象選択画面(C01-P020) へ遷移する
   * @returns
   */
  private getSpacesUrl(
    condition: PostJourneysSearchJourneyRequest,
    result: CheckinSearchResultState,
    amcMember: AMCMemberModel
  ) {
    this._checkinCommonStoreService.updateCheckinCommon({
      journeyId: result.model?.journeyId,
      searchType: condition.searchType,
    });
    this._aswServiceStoreService.updateAswService({
      orderId: condition.orderId,
      travelDocumentId: condition.eTicketNumber,
      firstName: condition.firstName,
      lastName: condition.lastName,
    });

    if (!!result.model?.journeyId) {
      const isAmcMember = condition.searchType === PostJourneysSearchJourneyRequest.SearchTypeEnum.AmcMember;
      const searchedTravelerName = isAmcMember
        ? amcMember.alteaName?.firstName + ' ' + amcMember.alteaName?.lastName
        : condition.firstName + ' ' + condition.lastName;

      return this.getCprCommonProcess$(result.model?.journeyId, {
        isMaskingRequired: true,
        isUnFlightsReturnRequired: true,
      }).pipe(
        map((cprResult) => {
          const NotAccepted = PostJourneysGetJourneyResponseDataTravelersInner.AcceptanceStatusSummaryEnum.NotAccepted;
          const checkinTargetTravelers = cprResult?.data.travelers.filter(
            (traveler: PostJourneysGetJourneyResponseDataTravelersInner) =>
              traveler.acceptanceStatusSummary === NotAccepted
          );

          this._checkinSearchResultStoreService.clear();

          const isExistsTravelers = (travelers?: PostJourneysGetJourneyResponseDataTravelersInner[]) => {
            return !!travelers && travelers.length > 0;
          };

          // 画面遷移前に設定する前画面ID
          const previousPageIdentifier = this.isInflowFromReservation()
            ? PREVIOUS_PAGE_IDENTIFIER.SERVICING_MY_BOOKING
            : PREVIOUS_PAGE_IDENTIFIER.CHECKIN_SEARCH;

          if (!isExistsTravelers(checkinTargetTravelers)) {
            // チェックイン対象搭乗者が存在しない場合、チェックイン状況詳細画面に遷移
            this._checkinCommonStoreService.updateCheckinCommon({
              previousPageIdentifier,
            });
            return CHECKIN_ROUTES.checkinMybooking;
          } else {
            // チェックイン対象搭乗者が存在しない場合、チェックイン対象選択画面に遷移
            this._checkinCommonStoreService.updateCheckinCommon({
              searchedTravelerName,
              previousPageIdentifier,
            });
            return CHECKIN_ROUTES.checkinSelect;
          }
        })
      );
    }

    return undefined;
  }

  /**
   * クエリ文字列の作成
   * 「key + “=” + value」形式で“&”(半角アンド)区切りで連結
   *
   * @param obj
   * @returns
   */
  private createQueryString(obj: Object): string {
    return Object.entries(obj)
      .map((pair) => pair.map(encodeURIComponent).join('='))
      .join('&');
  }

  /**
   * 外部流入パラメータをクリアする
   */
  private clearInflowParameter() {
    delete (window as any).Asw?.ApiRequestParam?.post;
  }
}
